import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Check, X, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import Logo from '../components/Logo';
import { PageHeader } from '../components/PageBits';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useApp } from '../context/AppContext';
import { AVATAR_CHOICES, LANGS, MATURITY } from '../lib/constants';
import { toast } from 'sonner';


function Sel({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 bg-[#1E1927] border-[#26212F] text-[#FFFFFF]" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#1E1927] border-[#26212F] text-[#FFFFFF]">{options.map((o) => <SelectItem key={o} value={o} className="focus:bg-[#2A2136] focus:text-[#FFFFFF]">{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ProfileEditor({ initial, onSave, onCancel, onDelete }) {
  const [p, setP] = useState(initial);
  return (
    <div className="surface p-5 fade-up" data-testid="profile-editor">
      <div className="flex flex-col md:flex-row gap-6">
        <div><img src={p.avatar} alt="" className="w-[120px] h-[120px] rounded-xl object-cover" /><div className="flex gap-1.5 mt-3 flex-wrap max-w-[130px]">{AVATAR_CHOICES.map((a) => <button key={a} onClick={() => setP({ ...p, avatar: a })} className={`w-9 h-9 rounded-md overflow-hidden ring-2 ${p.avatar === a ? 'ring-[#8B2CFF]' : 'ring-transparent'}`}><img src={a} alt="" className="w-full h-full object-cover" /></button>)}</div></div>
        <div className="flex-1 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2"><label className="text-[12px] text-[#C7C4CB]">Name</label><input className="clax-input mt-1" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="profile-name-input" /></div>
          <div><label className="text-[12px] text-[#C7C4CB]">Language</label><div className="mt-1"><Sel value={p.language} onChange={(v) => setP({ ...p, language: v })} options={LANGS} /></div></div>
          <div><label className="text-[12px] text-[#C7C4CB]">Maturity rating</label><div className="mt-1"><Sel value={p.maturity} onChange={(v) => setP({ ...p, maturity: v })} options={MATURITY} /></div></div>
          <div><label className="text-[12px] text-[#C7C4CB]">Default subtitles</label><div className="mt-1"><Sel value={p.subtitles} onChange={(v) => setP({ ...p, subtitles: v })} options={['Off', ...LANGS]} /></div></div>
          <div className="flex flex-col gap-3 justify-center">
            <label className="flex items-center justify-between text-[13px]"><span>Kids profile</span><Switch checked={p.kids} onCheckedChange={(v) => setP({ ...p, kids: v, maturity: v ? 'All' : p.maturity })} /></label>
            <label className="flex items-center justify-between text-[13px]"><span>Autoplay next episode</span><Switch checked={p.autoplay} onCheckedChange={(v) => setP({ ...p, autoplay: v })} /></label>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-5">
        <button onClick={() => onSave(p)} className="btn-brand !h-10" data-testid="profile-save"><Check size={15} /> Save</button>
        <button onClick={onCancel} className="btn-ghost !h-10"><X size={15} /> Cancel</button>
        {onDelete && <button onClick={onDelete} className="ml-auto text-[13px] text-[#FF6B81] hover:text-[#FFFFFF] flex items-center gap-1.5" data-testid="profile-delete"><Trash2 size={14} /> Delete profile</button>}
      </div>
    </div>
  );
}

export default function Profiles() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectMode = params.get('select') === '1';
  const { profiles, addProfile, updateProfile, deleteProfile, activeProfileId, setActiveProfileId, user, authLoading } = useApp();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const pick = (id) => { setActiveProfileId(id); if (selectMode) navigate('/'); else toast.success('Profile switched'); };

  if (!authLoading && !user) { navigate('/signin'); return null; }

  if (selectMode) {
    return (
      <div className="min-h-screen bg-[#0B0A0E] flex flex-col items-center justify-center p-6" data-testid="profile-select">
        <Logo height={44} />
        <h1 className="text-[32px] font-bold mt-8">Who's watching?</h1>
        <div className="flex flex-wrap justify-center gap-6 mt-10">
          {profiles.map((p) => (
            <button key={p.id} onClick={() => pick(p.id)} className="group text-center" data-testid={`select-profile-${p.id}`}>
              <div className="w-[130px] h-[130px] rounded-xl overflow-hidden ring-2 ring-transparent group-hover:ring-white transition"><img src={p.avatar} alt="" className="w-full h-full object-cover" /></div>
              <div className="mt-3 text-[15px] text-[#C7C4CB] group-hover:text-[#FFFFFF]">{p.name}{p.kids && <span className="ml-1.5 text-[10px] bg-[#8B2CFF]/15 text-[#8B2CFF] px-1.5 py-0.5 rounded">KIDS</span>}</div>
            </button>
          ))}
        </div>
        <button onClick={() => navigate('/profiles')} className="btn-ghost mt-12">Manage Profiles</button>
      </div>
    );
  }

  return (
    <Layout>
      <PageHeader title="Manage Profiles" subtitle="Up to 5 profiles per account. Each has its own list, history and settings." right={<button onClick={() => { setAdding(true); setEditing(null); }} className="btn-brand !h-10" data-testid="profiles-add"><Plus size={15} /> Add profile</button>} />
      <div className="px-[22px] pb-10 space-y-5">
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5" data-testid="profiles-grid">
          {profiles.map((p) => (
            <div key={p.id} className={`surface p-4 flex flex-col items-center text-center relative ${p.id === activeProfileId ? 'ring-1 ring-[#8B2CFF]' : ''}`} data-testid={`profile-card-${p.id}`}>
              <img src={p.avatar} alt="" className="w-[84px] h-[84px] rounded-xl object-cover" />
              <div className="mt-3 font-semibold text-[14px]">{p.name}</div>
              <div className="text-[11.5px] text-[#8A8694] mt-0.5 flex items-center gap-1">{p.kids && <Lock size={10} />}{p.maturity} &bull; {p.language}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => pick(p.id)} className={`chip !h-8 ${p.id === activeProfileId ? 'on' : ''}`}>{p.id === activeProfileId ? 'Active' : 'Switch'}</button>
                <button onClick={() => { setEditing(p.id); setAdding(false); }} className="chip !h-8 !px-2.5" aria-label="Edit" data-testid={`profile-edit-${p.id}`}><Pencil size={13} /></button>
              </div>
            </div>
          ))}
        </div>
        {adding && <ProfileEditor initial={{ id: `p${Date.now()}`, name: '', avatar: AVATAR_CHOICES[4], language: 'English', maturity: '18+', kids: false, autoplay: true, subtitles: 'Off' }} onCancel={() => setAdding(false)} onSave={async (p) => { if (!p.name.trim()) return toast.error('Name required'); const r = await addProfile(p); if (r) { setAdding(false); toast.success('Profile added'); } }} />}
        {editing && <ProfileEditor initial={profiles.find((p) => p.id === editing)} onCancel={() => setEditing(null)} onSave={async (p) => { const r = await updateProfile(p); if (r) { setEditing(null); toast.success('Profile updated'); } }} onDelete={profiles.length > 1 ? async () => { const ok = await deleteProfile(editing); if (ok) { setEditing(null); toast.success('Profile deleted'); } } : null} />}
      </div>
    </Layout>
  );
}
